/**
 * TutorPreCallScreen — le précall du masteur (`pre-call`, poussée par « GO ! ») : `<TutorPreCallPage>`,
 * la salle d'attente de l'élève dans les mots du masteur.
 *
 * Un fil, en deux temps (`useTutorPreCallState`) : tant que la séance n'est pas connue ou que le choix du
 * navigateur est à l'écran, la page sans test (l'entrée tourne, ou le choix prend la place) ; ensuite le
 * test du matériel monté (`TutorPreCallChecked`), sous sa frontière d'erreur. Les props du test sont celles
 * de l'entrée de l'élève (`liveEntryCheckProps`). Pas de bouton du micro sur l'image : l'ancien écran n'en
 * avait pas, et la salle du masteur ne le relit pas.
 */
import React from "react"

import { TutorPreCallPage } from "../../../../../../design-system/tutor"
import { liveEntryJoin } from "../../../../view-models/student/LiveEntry/liveEntryLobbyViewModel"
import { LiveEntryCheckBoundary } from "../../student/LiveEntry/LiveEntryCheckBoundary"
import { liveEntryBrowserChoice, liveEntryCheckProps } from "../../student/LiveEntry/liveEntryPageProps"
import { type TutorSpaceScreenProps } from "../tutorSpaceRouteTypes"
import {
    FAILED_CHECK_REPORT,
    useTutorPreCallCheck,
    useTutorPreCallEntry,
    type TutorPreCallEntry,
} from "./useTutorPreCallState"

/** Avant le test : la séance attendue (« On prépare la vérification… »), l'entrée « sans vérifier ». */
const WAITING_JOIN = liveEntryJoin(null)

/** Ce que les trois formes de la page partagent : la barre, l'heure, les élèves, l'image, le retour. */
type PageBase = ReturnType<typeof useTutorPreCallEntry>["page"] & {
    preview: { cameraOn: boolean; onToggleCamera: () => void }
    onBack: () => void
}

export function TutorPreCallScreen({ route }: TutorSpaceScreenProps) {
    const entry = useTutorPreCallEntry(String(route?.params?.liveSessionId ?? "0"))
    const base: PageBase = {
        ...entry.page,
        preview: { cameraOn: entry.camera.on, onToggleCamera: entry.camera.toggle },
        onBack: entry.onBack,
    }

    if (!entry.checkMounted) {
        return (
            <TutorPreCallPage
                {...base}
                checkPending
                browserChoice={liveEntryBrowserChoice(entry.browser, entry.dismissBrowser)}
                entering
                joinReady={WAITING_JOIN.ready}
                joinBeforeCheck={WAITING_JOIN.beforeCheck}
                joinNote={WAITING_JOIN.note}
                onEnter={() => {}}
            />
        )
    }

    // Le test tombé : la page sans lui, l'entrée pleine (`allDone` sans verdict, comme l'ancien).
    const fallback = <TutorPreCallPage {...base} joinReady onEnter={() => entry.openLive(FAILED_CHECK_REPORT)} />
    return (
        <LiveEntryCheckBoundary role="tutor" fallback={fallback}>
            <TutorPreCallChecked entry={entry} base={base} />
        </LiveEntryCheckBoundary>
    )
}

function TutorPreCallChecked({ entry, base }: { entry: TutorPreCallEntry; base: PageBase }) {
    const { check, vm, lobby, releasing, onEnter } = useTutorPreCallCheck(entry)
    return (
        <TutorPreCallPage
            {...base}
            {...liveEntryCheckProps({ equipment: lobby.equipment, check, vm, testShown: true, releasing })}
            entering={releasing}
            // Sans séance, pas de test guidé à finir : l'ancien servait l'entrée pleine (`PreCallScreen.js:149`).
            joinReady={lobby.join.ready || !entry.model.found}
            joinBeforeCheck={lobby.join.beforeCheck}
            joinNote={lobby.join.note}
            onEnter={onEnter}
        />
    )
}
